// Shared list pager — "‹ Prev  1 … 4 5 6 … 11  Next ›  ·  1–50 of 207". Page numbers are
// clickable for quick jumps (windowed with … for big ranges). Uses tina4css .page-link + brand .pager.
// Render it at the TOP and BOTTOM of a list (both inside the one reactive block that owns the rows).
// onPage(newPage) receives a clamped 1-based page; wire it to reload the list.
//
// IMPORTANT: always returns ONE <nav> element, never null. In this tina4js version a reactive
// `${() => ...}` block that toggles through null APPENDS its next template instead of replacing
// the old one. When there's nothing to page (single page) we render a hidden <nav> so the slot
// keeps a single stable anchor node — and so a 1-page list shows no pager clutter.
import { html } from './t4.js';
import { icon } from './icons.js';
import { t } from './i18n.js';

// Shared rows-per-page contract — keep every data list consistent. Small default by design.
export const PER_PAGE_OPTIONS = ['10', '20', '50', '100'];
export const DEFAULT_PER_PAGE = '20';

// Rows-per-page <select>. `per` is the current value (string); `onChange` writes it + reloads.
export function perPageSelect(per, onChange){
  return html`<select class="form-control per-select" .value=${String(per)} @change=${onChange}>
    ${PER_PAGE_OPTIONS.map(n => html`<option value=${n}>${n} / page</option>`)}
  </select>`;
}

// Windowed page list with … gaps: always first + last, plus ±1 around current. Small ranges show all.
function pageList(cur, total){
  if(total <= 7) return Array.from({ length: total }, (_, i) => i + 1);
  const out = [1];
  const lo = Math.max(2, cur - 1), hi = Math.min(total - 1, cur + 1);
  if(lo > 2) out.push('…');
  for(let i = lo; i <= hi; i++) out.push(i);
  if(hi < total - 1) out.push('…');
  out.push(total);
  return out;
}

export function pager({ page, totalPages, total, perPage, onPage }){
  const tp = Math.max(1, totalPages || 1);
  const p  = Math.min(Math.max(1, page || 1), tp);
  // Nothing to page (empty or single page) → stable hidden anchor, no clutter.
  if(!total || tp <= 1) return html`<nav class="pager pager-hidden" aria-hidden="true"></nav>`;
  const from = (p - 1) * perPage + 1;
  const to   = Math.min(total, p * perPage);
  return html`
    <nav class="pager" aria-label="Pagination">
      <button type="button" class="page-link pager-btn" ?disabled=${p <= 1} @click=${() => onPage(p - 1)}>${icon("chevron-left", {size: 14})} Prev</button>
      ${pageList(p, tp).map(n => n === '…'
        ? html`<span class="pager-gap">…</span>`
        : html`<button type="button" class=${'page-link pager-num' + (n === p ? ' active' : '')} @click=${() => onPage(n)}>${n}</button>`)}
      <button type="button" class="page-link pager-btn" ?disabled=${p >= tp} @click=${() => onPage(p + 1)}>Next ${icon("chevron-right", {size: 14})}</button>
      <span class="pager-info">${from}–${to} of ${total}</span>
    </nav>`;
}
